import React, { useState, useRef, useMemo } from 'react';
import api from '../../utils/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Info, X, Trash2 } from 'lucide-react';
import { SinVencimientoCheck } from './SinVencimientoCheck';

/**
 * Parsea un texto/CSV y devuelve un array de cuenta_ids únicos y limpios.
 * Soporta separadores: salto de línea, coma, punto y coma, tab, espacios.
 * Si la primera línea contiene un header de tipo "cuenta", "cuit", "cvu" o "cbu", se omite.
 */
const parseCuentas = (text) => {
  if (!text) return [];
  const lines = text.split(/\r?\n/);

  // Detectar header
  let startIdx = 0;
  if (lines.length > 0) {
    const headerCandidate = lines[0].toLowerCase().trim();
    if (
      /(^|[,;\s])(cuenta|cuit|cvu|cbu|account|id)([,;\s]|$)/.test(headerCandidate) &&
      !/\d{8,}/.test(headerCandidate)
    ) {
      startIdx = 1;
    }
  }

  const out = [];
  const seen = new Set();
  for (let i = startIdx; i < lines.length; i++) {
    const tokens = lines[i].split(/[,;\t]+/);
    for (const tok of tokens) {
      const t = tok.trim().replace(/^["']|["']$/g, '');
      if (!t) continue;
      if (!seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
  }
  return out;
};

const TIPO_LABELS = {
  WHITELIST: 'Whitelist (Confiable)',
  BLACKLIST: 'Blacklist (Bloquear)',
};

export const BulkUploadModal = ({ open, onClose, onSuccess, defaultTipo = 'WHITELIST' }) => {
  const [tipo, setTipo] = useState(defaultTipo);
  const [motivo, setMotivo] = useState('');
  const [fechaExpiracion, setFechaExpiracion] = useState('');
  const [sinVencimiento, setSinVencimiento] = useState(false);
  const [rawText, setRawText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const cuentasParsed = useMemo(() => parseCuentas(rawText), [rawText]);

  const resetState = () => {
    setTipo(defaultTipo);
    setMotivo('');
    setFechaExpiracion('');
    setSinVencimiento(false);
    setRawText('');
    setResult(null);
    setSubmitting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const handleFile = async (file) => {
    if (!file) return;
    const isCsvLike =
      /\.(csv|txt|tsv)$/i.test(file.name) ||
      file.type === 'text/csv' ||
      file.type === 'text/plain';

    if (!isCsvLike) {
      toast.error('Formato no soportado. Usa CSV, TSV o TXT (una cuenta por línea).');
      return;
    }
    try {
      const text = await file.text();
      setRawText((prev) => (prev ? prev + '\n' + text : text));
      toast.success(`Archivo "${file.name}" cargado`);
    } catch (e) {
      toast.error('No se pudo leer el archivo');
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSubmit = async () => {
    if (cuentasParsed.length === 0) {
      toast.error('Ingresa al menos una cuenta válida');
      return;
    }
    if (!motivo.trim()) {
      toast.error('El motivo es obligatorio');
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const payload = {
        cuentas: cuentasParsed,
        tipo,
        motivo: motivo.trim(),
        fecha_expiracion: (tipo === 'WHITELIST' && sinVencimiento) ? null : (fechaExpiracion || null),
        sin_vencimiento: tipo === 'WHITELIST' && sinVencimiento,
      };
      const res = await api.post('/account-lists/bulk', payload);
      setResult(res.data);
      const { creadas, duplicadas, errores, total } = res.data;
      if (creadas > 0) {
        toast.success(
          `Carga masiva completada: ${creadas} creadas · ${duplicadas} duplicadas · ${errores} errores (de ${total})`,
        );
      } else if (duplicadas === total) {
        toast.info('Todas las cuentas ya estaban en la lista');
      } else {
        toast.error('No se pudo crear ninguna cuenta');
      }
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la carga masiva');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#ffffff' }}
        data-testid="bulk-upload-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Upload size={20} className="text-blue-600" />
            Carga Masiva de Cuentas
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Agrega múltiples cuentas a la lista en una sola operación. Todas comparten los mismos
            metadatos.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 py-2">
            {/* Tipo */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-700">Tipo de Lista *</Label>
                <Select value={tipo} onValueChange={setTipo}>
                  <SelectTrigger style={{ backgroundColor: '#ffffff' }} data-testid="bulk-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WHITELIST">{TIPO_LABELS.WHITELIST}</SelectItem>
                    <SelectItem value="BLACKLIST">{TIPO_LABELS.BLACKLIST}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">{tipo === 'WHITELIST' ? 'Fecha de vencimiento *' : 'Fecha de Expiración (opcional)'}</Label>
                <Input
                  type="date"
                  value={fechaExpiracion}
                  onChange={(e) => setFechaExpiracion(e.target.value)}
                  disabled={tipo === 'WHITELIST' && sinVencimiento}
                  style={{ backgroundColor: '#ffffff' }}
                  data-testid="bulk-fecha-expiracion"
                />
                {tipo === 'WHITELIST' && (
                  <SinVencimientoCheck checked={sinVencimiento} onChange={setSinVencimiento} testid="bulk-sin-vencimiento" />
                )}
              </div>
            </div>

            {/* Motivo compartido */}
            <div>
              <Label className="text-slate-700">Motivo compartido *</Label>
              <Input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Razón aplicada a todas las cuentas cargadas..."
                style={{ backgroundColor: '#ffffff' }}
                data-testid="bulk-motivo-input"
              />
            </div>

            {/* Entrada por texto */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-slate-700">Cuentas (una por línea, o separadas por coma/punto y coma)</Label>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span data-testid="bulk-parsed-count" className="font-medium text-slate-700">
                    {cuentasParsed.length}
                  </span>
                  <span>detectadas</span>
                </div>
              </div>
              <Textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder={`30-12345678-9\n0000003100012345678901\n0000003100012345678902\n...`}
                className="min-h-[140px] font-mono text-sm"
                style={{ backgroundColor: '#ffffff' }}
                data-testid="bulk-cuentas-textarea"
              />
            </div>

            {/* Drag & drop / file input */}
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors"
              data-testid="bulk-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.tsv,text/csv,text/plain"
                onChange={onFileChange}
                className="hidden"
                data-testid="bulk-file-input"
              />
              <FileSpreadsheet size={28} className="mx-auto text-slate-400 mb-1" />
              <p className="text-sm text-slate-600">
                Arrastra un CSV/TXT aquí o{' '}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-blue-600 hover:text-blue-800 font-medium underline"
                  data-testid="bulk-file-browse-btn"
                >
                  selecciónalo
                </button>
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Una cuenta por línea. Se detecta automáticamente un header (cuenta, cuit, cvu, cbu).
              </p>
            </div>

            {/* Preview */}
            {cuentasParsed.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Info size={14} />
                    Vista previa ({cuentasParsed.length} cuentas únicas)
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setRawText('')}
                    className="text-slate-500 hover:text-red-600"
                    data-testid="bulk-clear-btn"
                  >
                    <Trash2 size={14} className="mr-1" />
                    Limpiar
                  </Button>
                </div>
                <div className="max-h-32 overflow-y-auto flex flex-wrap gap-1">
                  {cuentasParsed.slice(0, 50).map((c) => (
                    <span
                      key={c}
                      className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs font-mono text-slate-700"
                    >
                      {c}
                    </span>
                  ))}
                  {cuentasParsed.length > 50 && (
                    <span className="px-2 py-0.5 text-xs text-slate-500 italic">
                      +{cuentasParsed.length - 50} más...
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* RESULTADO */
          <div className="space-y-4 py-2" data-testid="bulk-result-view">
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{result.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600" data-testid="bulk-result-creadas">
                  {result.creadas}
                </p>
                <p className="text-xs text-green-700">Creadas</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-600" data-testid="bulk-result-duplicadas">
                  {result.duplicadas}
                </p>
                <p className="text-xs text-amber-700">Duplicadas</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-600" data-testid="bulk-result-errores">
                  {result.errores}
                </p>
                <p className="text-xs text-red-700">Errores</p>
              </div>
            </div>

            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                      Cuenta
                    </th>
                    <th className="text-center px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                      Estado
                    </th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                      Detalle
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.items.map((it, idx) => (
                    <tr key={`${it.cuenta_id}-${idx}`}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-800">{it.cuenta_id}</td>
                      <td className="px-3 py-2 text-center">
                        {it.status === 'created' && (
                          <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
                            <CheckCircle2 size={14} /> Creada
                          </span>
                        )}
                        {it.status === 'duplicate' && (
                          <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium">
                            <Info size={14} /> Duplicada
                          </span>
                        )}
                        {it.status === 'error' && (
                          <span className="inline-flex items-center gap-1 text-red-700 text-xs font-medium">
                            <AlertCircle size={14} /> Error
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{it.detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                style={{ color: '#374151' }}
                data-testid="bulk-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || cuentasParsed.length === 0 || !motivo.trim()}
                style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
                data-testid="bulk-submit-btn"
              >
                {submitting
                  ? 'Procesando...'
                  : `Cargar ${cuentasParsed.length || ''} cuenta${cuentasParsed.length === 1 ? '' : 's'}`}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setResult(null);
                  setRawText('');
                  setMotivo('');
                  setFechaExpiracion('');
                }}
                style={{ color: '#374151' }}
                data-testid="bulk-result-new-btn"
              >
                Nueva carga
              </Button>
              <Button
                onClick={handleClose}
                style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
                data-testid="bulk-result-close-btn"
              >
                Cerrar
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadModal;
